import { ApiClient } from "../ApiClient";
import { FinancialReportType } from "../../lib/Constants";
import { apiFailureFromResponse } from "../../utils/api/parseApiErrorMessage";

/**
 * Fetch all generated report periods for the authenticated user
 * @param {string} reportType - Report type code
 * @returns {Promise<Object>} API response containing array of report periods
 */
export const fetchReportPeriods = async (
  reportType = FinancialReportType.MONTHLY_FINANCIAL_SUMMARY,
) => {
  const response = await ApiClient.get(`/finances/reports?type=${reportType}`);

  if (response.ok) {
    const result = await response.json();
    const rawResults = result.results || [];

    const transformedReports = rawResults.map((r) => ({
      uuid: r.uuid,
      report_type: r.report_type,
      period_key: r.period_key,
      period_start: r.period_start,
      period_end: r.period_end,
      plan_tier_at_generation: r.plan_tier_at_generation,
      generated_at: r.generated_at,
      has_data: r.has_data,
      has_pdf: r.has_pdf,
    }));

    return {
      success: true,
      data: transformedReports,
      status: response.status,
    };
  }

  return apiFailureFromResponse(
    response,
    "Failed to fetch financial report periods",
  );
};

/**
 * Fetch detail and snapshot JSON for a specific financial report
 * @param {string} reportUuid - UUID of the report snapshot
 * @returns {Promise<Object>} API response containing full snapshot payload
 */
export const fetchReportDetail = async (reportUuid) => {
  const response = await ApiClient.get(`/finances/reports/${reportUuid}`);

  if (response.ok) {
    const result = await response.json();

    return {
      success: true,
      data: {
        uuid: result.uuid,
        report_type: result.report_type,
        period_key: result.period_key,
        period_start: result.period_start,
        period_end: result.period_end,
        plan_tier_at_generation: result.plan_tier_at_generation,
        generated_at: result.generated_at,
        has_data: result.has_data,
        has_pdf: result.has_pdf,
        snapshot: result.snapshot || {},
      },
      status: response.status,
    };
  }

  return apiFailureFromResponse(response, "Failed to fetch report details");
};

/**
 * Download PDF or CSV export for a paid financial report snapshot
 * @param {string} reportUuid - UUID of the report snapshot
 * @param {string} format - Export format ('pdf' or 'csv'), defaults to 'pdf'
 * @returns {Promise<Object>} Object containing file blob and filename or error
 */
export const downloadReportPdf = async (reportUuid, format = "pdf") => {
  const response = await ApiClient.get(
    `/finances/export-report/${reportUuid}?format=${format}`,
  );

  if (response.ok) {
    const blob = await response.blob();

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers.get("content-disposition");
    let filename = `MFS_Report.${format}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    return {
      success: true,
      blob,
      filename,
    };
  }

  return apiFailureFromResponse(response, "Failed to download report export.");
};
